import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Mail, Loader2, Eye, EyeOff, ShieldCheck, KeyRound, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();

  // ── Login state ───────────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  // ── First-login change-password state ─────────────────────────────────────
  const [mustChange, setMustChange] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [pendingName, setPendingName] = useState(null);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [savingNewPwd, setSavingNewPwd] = useState(false);

  // ── Login submit ──────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Introduce email y contraseña'); return; }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/admin/login`, { email, password });
      const { access_token, name, must_change_password } = resp.data;
      if (must_change_password) {
        // Show change-password screen before storing the session (force change)
        setPendingToken(access_token);
        setPendingName(name);
        setCurrentPwd(password);   // pre-fill current password
        setNewPwd(''); setConfirmPwd('');
        setMustChange(true);
      } else {
        localStorage.setItem('admin_token', access_token);
        localStorage.setItem('admin_name', name);
        toast.success(`Bienvenido, ${name}`);
        navigate('/admin');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Credenciales incorrectas';
      toast.error(typeof msg === 'string' ? msg : 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  // ── Change password submit ────────────────────────────────────────────────
  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (newPwd !== confirmPwd) { toast.error('Las contraseñas no coinciden'); return; }
    if (newPwd === currentPwd) { toast.error('La nueva contraseña debe ser distinta de la actual'); return; }
    setSavingNewPwd(true);
    try {
      await axios.post(
        `${API}/admin/me/change-password`,
        { current_password: currentPwd, new_password: newPwd },
        { headers: { Authorization: `Bearer ${pendingToken}` } }
      );
      // Persist session now
      localStorage.setItem('admin_token', pendingToken);
      localStorage.setItem('admin_name', pendingName || 'Admin');
      toast.success('Contraseña actualizada. ¡Bienvenido!');
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg).join(' · ')
        : (detail || 'Error al cambiar contraseña');
      toast.error(msg);
    } finally {
      setSavingNewPwd(false);
    }
  };

  // ── Strength helper ───────────────────────────────────────────────────────
  const pwdStrength = (() => {
    const checks = [
      newPwd.length >= 8,
      /[a-z]/.test(newPwd),
      /[A-Z]/.test(newPwd),
      /\d/.test(newPwd),
      /[^a-zA-Z0-9]/.test(newPwd),
    ];
    const score = checks.filter(Boolean).length;
    return {
      score,
      label: ['Muy débil', 'Débil', 'Aceptable', 'Buena', 'Fuerte', 'Excelente'][score],
      color: ['bg-red-500', 'bg-red-500', 'bg-amber-500', 'bg-yellow-500', 'bg-green-500', 'bg-green-600'][score],
    };
  })();

  // ── Render: change password ───────────────────────────────────────────────
  if (mustChange) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex flex-col items-center justify-center px-4 py-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#009940] to-[#69C7EA] flex items-center justify-center shadow-lg mb-3">
            <KeyRound size={26} className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-[#374B67]">Cambia tu contraseña</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-md">
            Por seguridad, debes establecer una nueva contraseña antes de continuar al panel.
          </p>
        </div>

        <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 mb-5 flex items-start gap-2">
            <ShieldCheck size={14} className="mt-0.5 flex-shrink-0 text-amber-700" />
            <span>Conectado como <strong>{email}</strong>. Tu sesión se iniciará al guardar.</span>
          </div>

          <form onSubmit={handleChangePwd} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contraseña actual</label>
              <input
                type="password"
                value={currentPwd}
                onChange={e => setCurrentPwd(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA] bg-slate-50"
                data-testid="change-current-pwd"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nueva contraseña</label>
              <div className="relative">
                <input
                  type={showNewPwd ? 'text' : 'password'}
                  value={newPwd}
                  onChange={e => setNewPwd(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl pl-4 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
                  data-testid="change-new-pwd"
                  required
                  minLength={8}
                />
                <button type="button" onClick={() => setShowNewPwd(!showNewPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showNewPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {newPwd && (
                <div className="mt-2">
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${pwdStrength.color} transition-all duration-200`}
                      style={{ width: `${(pwdStrength.score / 5) * 100}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Fortaleza: <strong>{pwdStrength.label}</strong></p>
                </div>
              )}
              <ul className="mt-2 text-[11px] text-slate-500 space-y-0.5">
                <li className={newPwd.length >= 8 ? 'text-green-700' : ''}>
                  {newPwd.length >= 8 ? '✓' : '•'} Al menos 8 caracteres
                </li>
                <li className={/\d/.test(newPwd) && /[a-zA-Z]/.test(newPwd) ? 'text-green-700' : ''}>
                  {/\d/.test(newPwd) && /[a-zA-Z]/.test(newPwd) ? '✓' : '•'} Combina letras y números
                </li>
              </ul>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirmar nueva contraseña</label>
              <input
                type={showNewPwd ? 'text' : 'password'}
                value={confirmPwd}
                onChange={e => setConfirmPwd(e.target.value)}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                  confirmPwd && newPwd !== confirmPwd
                    ? 'border-red-300 focus:ring-red-300'
                    : 'border-slate-200 focus:ring-[#69C7EA]'
                }`}
                data-testid="change-confirm-pwd"
                required
                minLength={8}
              />
              {confirmPwd && newPwd !== confirmPwd && (
                <p className="text-[11px] text-red-600 mt-1">Las contraseñas no coinciden</p>
              )}
            </div>
            <button
              type="submit"
              disabled={savingNewPwd}
              className="w-full bg-[#009940] hover:bg-[#007F35] text-white py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="change-submit-button"
            >
              {savingNewPwd
                ? <><Loader2 size={16} className="animate-spin" /> Guardando…</>
                : <><CheckCircle2 size={16} /> Cambiar contraseña y entrar</>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Render: login ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F7F9FC] flex flex-col items-center justify-center px-4">
      <div className="text-center mb-8">
        <img
          src="https://customer-assets.emergentagent.com/job_majadahonda-gis/artifacts/i574mq3v_image.png"
          alt="Ayuntamiento de Majadahonda"
          className="h-14 object-contain mx-auto mb-3"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <h1 className="text-2xl font-semibold text-[#374B67]">Panel Administrativo</h1>
        <p className="text-slate-500 text-sm mt-1">Recogida de Enseres · Majadahonda</p>
      </div>

      <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <h2 className="text-lg font-semibold text-[#374B67] mb-6 text-center">Iniciar sesión</h2>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="usuario@dominio.es"
                className="w-full border border-slate-200 rounded-xl pl-9 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
                data-testid="admin-email-input"
                autoComplete="email"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Contraseña</label>
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full border border-slate-200 rounded-xl pl-9 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#69C7EA]"
                data-testid="admin-password-input"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#009940] hover:bg-[#007F35] text-white py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
            data-testid="admin-login-button"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Accediendo...</> : 'Ingresar'}
          </button>
        </form>
        <p className="text-xs text-center text-slate-400 mt-4">
          Si es la primera vez, te pediremos cambiar la contraseña.
        </p>
      </div>
      <p className="text-xs text-slate-400 mt-6">
        <a href="/" className="hover:text-[#374B67]">← Volver al portal ciudadano</a>
      </p>
    </div>
  );
}
